import useOiReferenceLabels from '../../hooks/useOiReferenceLabels';
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Info, AlertCircle, X } from 'lucide-react';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import IsolationsAndInstruction from './IsolationsAndInstructions';
import Earths from './earths';
import Signature from './signature';
import {
  createHVWorkPermit,
  saveEarths,
  saveIsolations,
  updateEarth,
  updateIsolation,
  saveSignature,
  updateSignature,
  updateHVWorkPermit,
  updateHVWorkPermitDraft,
  getHVWorkPermitById,
  getIsolationById,
  getEarthById,
} from '../../services/hvWorkPermitService';
import { getCrossReference, getLocation, getPlantcodes, getStatus, getKeys } from '../../services/locationService';
import { getDepartments } from '../../services/departmentService';

const emptyIsolationDraft = {
  plantCode: '',
  location: '',
  description: '',
  requirements: false,
  status: '',
  key: '',
  ops: '',
};

const emptyEarthDraft = {
  plantCode: '',
  location: '',
  description: '',
  requirements: false,
  status: '',
  key: '',
  ops: '',
};

/* ================= TYPES ================= */

interface FormValues {
  department: string;
  priority: string;
  jobNumber: string;
  ewt: string;
  locationId: string;
  crossReferenceIds: string[];
  unit: string;
  plantCodeId: string;
  apparatus: string;
  hazards: string;
  workDescription: string;
  referenceNumber?: string;
  status?: string;
}

interface DropdownItem {
  id: number;
  title: string;
  reference_number: string;
}

interface Department {
  id: number;
  name: string;
}

interface HVWorkPermitModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onEditSuccess?: (wasEditing: boolean) => void; // CHANGED: passes wasEditing boolean so parent knows add vs edit
  editId?: number | null;
}

const getStatusColors = (status: string) => {
  const s = status?.toLowerCase() || '';
  if (s === 'new' || s === 'pending') return { text: 'text-purple-600', dot: 'bg-purple-600', badge: 'text-purple-600 bg-purple-50' };
  if (s === 'isolation pending') return { text: 'text-orange-500', dot: 'bg-orange-500', badge: 'text-orange-500 bg-orange-50' };
  if (s === 'permit taken out') return { text: 'text-purple-500', dot: 'bg-purple-500', badge: 'text-purple-500 bg-purple-50' };
  if (s === 'issue permit pending') return { text: 'text-orange-500', dot: 'bg-orange-500', badge: 'text-orange-500 bg-orange-50' };
  if (s === 'permit issued') return { text: 'text-pink-600', dot: 'bg-pink-600', badge: 'text-pink-600 bg-pink-50' };
  if (s === 'rp change pending') return { text: 'text-orange-600', dot: 'bg-orange-600', badge: 'text-orange-600 bg-orange-50' };
  if (s === 'rp changed') return { text: 'text-green-600', dot: 'bg-green-600', badge: 'text-green-600 bg-green-50' };
  if (s === 'clearance done') return { text: 'text-green-500', dot: 'bg-green-500', badge: 'text-green-500 bg-green-50' };
  if (s === 'clearance pending') return { text: 'text-purple-500', dot: 'bg-purple-500', badge: 'text-purple-500 bg-purple-50' };
  if (s === 'isolation effected' || s === 'in progress')
    return { text: 'text-orange-500', dot: 'bg-orange-500', badge: 'text-orange-500 bg-orange-50' };
  if (s === 'clearance done (ao)' || s === 'completed' || s === 'approved')
    return { text: 'text-green-600', dot: 'bg-green-600', badge: 'text-green-600 bg-green-50' };
  return { text: 'text-gray-600', dot: 'bg-gray-600', badge: 'text-gray-600 bg-gray-50' };
};

const getStatusColor = (status: string) => {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower === 'in progress') return { dot: 'bg-orange-500', text: 'text-orange-500' };
  if (statusLower === 'completed') return { dot: 'bg-green-500', text: 'text-green-500' };
  if (statusLower === 'pending') return { dot: 'bg-gray-500', text: 'text-gray-500' };
  if (statusLower === 'on hold') return { dot: 'bg-yellow-500', text: 'text-yellow-500' };
  return { dot: 'bg-gray-500', text: 'text-gray-500' };
};

const isRequirementEnabled = (value: unknown): boolean =>
  value === true || value === 1 || (typeof value === 'string' && ['yes', 'true', '1'].includes(value.toLowerCase()));
/* =============== VALIDATION SCHEMA =============== */

const step0ValidationSchema = Yup.object({
  department: Yup.string().required('Department is required'),
  priority: Yup.string().required('Priority is required'),
  plantCodeId: Yup.string().required('Plant Code is required'),
  crossReferenceIds: Yup.array().of(Yup.string()).notRequired(),
});

const getValidationSchema = (currentStep: number) => {
  switch (currentStep) {
    case 0:
      return step0ValidationSchema;
    default:
      return Yup.object({});
  }
};

/* =============== RICH TEXT =============== */

const RichTextEditor = ({ value, onChange, placeholder, error, touched, maxLength }: any) => {
  const remaining = maxLength !== undefined ? maxLength - (value?.length || 0) : null;

  return (
    <div>
      <div className="border border-gray-300 rounded-md bg-gray-100">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
          <button type="button" className="p-1 hover:bg-gray-200 rounded">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M3 10h18M3 14h18" strokeWidth="2" />
            </svg>
          </button>
          <button type="button" className="p-1 hover:bg-gray-200 rounded">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M4 6h16M4 12h16M4 18h16" strokeWidth="2" />
            </svg>
          </button>
          <div className="flex items-center gap-1 ml-2">
            <span className="text-sm text-gray-600">Normal</span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </div>
          <div className="h-5 w-px bg-gray-300 mx-1" />
          <div className="flex items-center gap-1">
            <div className="w-5 h-5 bg-blue-600 rounded border border-gray-300" />
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </div>
          <button type="button" className="p-1 hover:bg-gray-200 rounded font-bold">
            B
          </button>
          <button type="button" className="p-1 hover:bg-gray-200 rounded italic">
            I
          </button>
          <button type="button" className="p-1 hover:bg-gray-200 rounded underline">
            U
          </button>
          <button type="button" className="p-1 hover:bg-gray-200 rounded line-through">
            S
          </button>
        </div>
        <textarea
          value={value}
          onChange={(e) => {
            if (maxLength !== undefined && e.target.value.length > maxLength) return;
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          className={`w-full px-4 py-3 min-h-[120px] resize-none focus:outline-none text-gray-900 placeholder-gray-400 bg-gray-100 ${
            error && touched ? 'border-red-500' : ''
          }`}
        />
      </div>
      <div className="flex justify-between items-center mt-1">
        {error && touched ? <div className="text-red-500 text-sm">{error}</div> : <div />}
        {maxLength !== undefined && (
          <div className={`text-xs ml-auto ${remaining !== null && remaining <= 50 ? 'text-red-500' : 'text-gray-400'}`}>
            {value?.length || 0}/{maxLength}
          </div>
        )}
      </div>
    </div>
  );
};

/* =============== ERROR MESSAGE COMPONENT =============== */

const FormError = ({ name }: { name: string }) => (
  <ErrorMessage name={name}>{(msg) => <div className="text-red-500 text-sm mt-1">{msg}</div>}</ErrorMessage>
);

/* =============== MODAL ================= */

const HVWorkPermitModal = ({ isOpen = true, onClose = () => {}, onEditSuccess = () => {}, editId = null }: HVWorkPermitModalProps) => {
  const steps = ['Application', 'Isolations/Instructions', 'Earths', 'Signature', editId ? 'Preview & Update' : 'Preview & Add'];

  const [permitId, setPermitId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [locations, setLocations] = useState<DropdownItem[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isolationData, setIsolationData] = useState<any>(null);
  const [triggerIsolationSubmit, setTriggerIsolationSubmit] = useState(0);
  const [earthsData, setEarthsData] = useState<any>(null);
  const [triggerEarthSubmit, setTriggerEarthSubmit] = useState(0);
  const [signatureData, setSignatureData] = useState<any>(null);
  const [applicationData, setApplicationData] = useState<FormValues | null>(null);

  const getOiReferenceLabels = useOiReferenceLabels(isOpen);
  const [existingIsolations, setExistingIsolations] = useState<any[]>([]);

  const [isolationsSaved, setIsolationsSaved] = useState(false);

  const [existingEarths, setExistingEarths] = useState<any[]>([]);
  const [signatureId, setSignatureId] = useState<number>(null);

  const [earthsSaved, setEarthsSaved] = useState(false);

  const [crossReferences, setCrossReferences] = useState<DropdownItem[]>([]);
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [keyOptions, setKeyOptions] = useState<any[]>([]);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [plantCodes, setPlantCodes] = useState<DropdownItem[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [isCrossRefDropdownOpen, setIsCrossRefDropdownOpen] = useState(false);

  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);

  const signatureRef = useRef<any>(null);
  const earthsRef = useRef<any>(null);
  const isolationRef = useRef<any>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);

  const [hasUnsavedData, setHasUnsavedData] = useState(false);

  const [earthFormData, setEarthFormData] = useState(null);

  const [isolationDraft, setIsolationDraft] = useState(emptyIsolationDraft);

  const [earthDraft, setEarthDraft] = useState(emptyEarthDraft);

  const [confirmSaving, setConfirmSaving] = useState(false);

  const tagsRef = useRef<HTMLDivElement>(null);
  const crossRefWrapperRef = useRef<HTMLDivElement>(null);

  const [crossRefSearch, setCrossRefSearch] = useState('');

  const initialValues: FormValues = applicationData || {
    department: '',
    priority: '',
    jobNumber: '',
    ewt: '',
    locationId: '',
    crossReferenceIds: [],
    unit: '',
    plantCodeId: '',
    apparatus: '',
    hazards: '',
    workDescription: '',
  };

  const liveFormValuesRef = useRef<FormValues>({
    department: '',
    priority: '',
    jobNumber: '',
    ewt: '',
    locationId: '',
    crossReferenceIds: [],
    unit: '',
    plantCodeId: '',
    apparatus: '',
    hazards: '',
    workDescription: '',
  });

  useEffect(() => {
    if (!isOpen) {
      setStep(0);
      setPermitId(null);
      setIsolationData(null);
      setSignatureId(null);
      setEarthsData(null);
      setSignatureData(null);
      setApplicationData(null);
      setExistingIsolations([]);
      setIsolationsSaved(false);
      setExistingEarths([]);
      setEarthsSaved(false);
      setHasUnsavedData(false);
      setIsolationDraft(emptyIsolationDraft);
      setEarthDraft(emptyEarthDraft);

      setEarthFormData(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchAllDropdownData = async () => {
      try {
        setIsLoadingLocations(true);
        setIsLoadingDepartments(true);

        const [locationsRes, crossRes, plantRes, deptRes, statusRes, keysRes] = await Promise.all([
          getLocation(),
          getCrossReference(),
          getPlantcodes(),
          getDepartments(),
          getStatus(),
          getKeys(),
        ]);

        setLocations(locationsRes);
        setCrossReferences(crossRes);
        setPlantCodes(plantRes);
        setDepartments(deptRes || []);
        setStatusOptions(statusRes || []);
        setKeyOptions(keysRes || []);

        setIsLoadingLocations(false);
        setIsLoadingDepartments(false);

        if (editId) {
          setPermitId(editId);
          await fetchWorkPermitData(editId, plantRes, locationsRes, crossRes, deptRes || []);
        }
      } catch (error: any) {
        console.error('Error loading dropdown data:', error);
        const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to load dropdown data';
        setApiError(message);
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
        setIsLoadingLocations(false);
        setIsLoadingDepartments(false);
      }
    };

    fetchAllDropdownData();
  }, [isOpen, editId]);

  useEffect(() => {}, [existingIsolations]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (crossRefWrapperRef.current && !crossRefWrapperRef.current.contains(event.target as Node)) {
        setIsCrossRefDropdownOpen(false);
        setCrossRefSearch('');
      }
    };

    if (isCrossRefDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCrossRefDropdownOpen]);

  const fetchWorkPermitData = async (
    id: number,
    plantCodesData: DropdownItem[] = [],
    locationsData: DropdownItem[] = [],
    crossRefsData: DropdownItem[] = [],
    deptsData: Department[] = [],
  ) => {
    try {
      setLoading(true);

      const permitResponse = await getHVWorkPermitById(id);

      const raw = permitResponse.data;
      const data = raw?.hv_work_permit || raw;

      const isolationId = data.isolations ?? data.isolation_id ?? data.isolation?.id ?? null;
      const signId = data.signature ?? data.signature_id ?? data.signature?.id ?? null;
      if (signId) {
        setSignatureId(signId);
      }

      const earthId =
        (typeof data.earth === 'object' ? data.earth?.id : data.earth) ??
        data.earth_id ??
        (typeof data.earths === 'object' ? data.earths?.id : data.earths) ??
        null;

      const isolationsResponse = isolationId
        ? { data: [(await getIsolationById(isolationId).catch(() => null))?.data].filter(Boolean) } // CHANGED: unwrap .data
        : { data: [] };

      const earthsResponse = earthId
        ? { data: [await getEarthById(earthId).catch(() => null)].filter(Boolean) } // CHANGED: unwrap .data, matches isolation fix
        : { data: [] };

      const departmentId = data.department_id?.toString() || deptsData.find((d) => d.name === data.department)?.id?.toString() || '';

      const plantCodeId = data.plant_code_id?.toString() || plantCodesData.find((pc) => pc.title === data.plant_code)?.id?.toString() || '';

      const locationId = data.location_id?.toString() || locationsData.find((loc) => loc.title === data.location)?.id?.toString() || '';

      // const crossReferenceId =
      //   data.cross_reference_id?.toString() || crossRefsData.find((cr) => cr.title === data.cross_reference)?.id?.toString() || '';
      const crossReferenceIds = Array.isArray(data.cross_reference_ids)
        ? data.cross_reference_ids.map((cid: number) => cid.toString())
        : [];

      setApplicationData({
        department: departmentId,
        priority: data.priority || '',
        jobNumber: data.job_number || '',
        ewt: data.ewt || '',
        locationId: locationId,
        crossReferenceIds: crossReferenceIds,
        unit: data.unit || '',
        plantCodeId: plantCodeId,
        apparatus: data.apparatus || '',
        hazards: data.description || '',
        workDescription: data.work || '',
        referenceNumber: data.reference_number || '',
        status: data.status || '',
      });

      const rawIsolations: any[] = Array.isArray(isolationsResponse?.data)
        ? isolationsResponse.data
        : isolationsResponse?.data
          ? [isolationsResponse.data]
          : [];

      const resolvedIsolations = rawIsolations.map((isoRecord: any) => {
        const plantCode = plantCodesData.find((pc) => pc.id === isoRecord.plant_code);
        const location = locationsData.find((loc) => loc.id === isoRecord.location);

        const items = Array.isArray(isoRecord.items) ? isoRecord.items : [];

        return {
          id: isoRecord.id,
          hv_work_permit_id: isoRecord.hv_work_permit?.id ?? id,
          plant_code: plantCode || null,
          plant_code_id: plantCode?.id?.toString() || '',
          location: location || null,
          location_id: location?.id?.toString() || '',
          description: isoRecord.describe_components || '',
          requirements: isRequirementEnabled(isoRecord.requirements),
          items: items.map((item: any) => ({
            id: item.id,
            status: item.status_id?.toString() || '',
            key: item.key_id?.toString() || '',
            ops: item.ops || '',
            oi_references: item.oi_references,
            oi_reference: item.oi_reference,
            oi_reference_ids: (Array.isArray(item.oi_reference_ids)
              ? item.oi_reference_ids
              : item.oi_reference_id != null
                ? [item.oi_reference_id]
                : Array.isArray(item.oi_references)
                  ? item.oi_references.map((reference: any) => reference.id)
                  : item.oi_reference?.id != null
                    ? [item.oi_reference.id]
                    : []
            ).map(Number),
          })),
        };
      });

      setExistingIsolations(resolvedIsolations);

      if (resolvedIsolations.length > 0) {
        setIsolationsSaved(true);
      }

      const rawEarths: any[] = Array.isArray(earthsResponse?.data)
        ? earthsResponse.data
        : earthsResponse?.data
          ? [earthsResponse.data]
          : [];

      const resolvedEarths = rawEarths.map((isoRecord: any) => {
        const plantCode = plantCodesData.find((pc) => pc.id === isoRecord.plant_code);
        const location = locationsData.find((loc) => loc.id === isoRecord.location);

        const items = Array.isArray(isoRecord.items) ? isoRecord.items : [];

        return {
          id: isoRecord.id,
          hv_work_permit_id: isoRecord.hv_work_permit?.id ?? id,
          plant_code: plantCode || null,
          plant_code_id: plantCode?.id?.toString() || '',
          location: location || null,
          location_id: location?.id?.toString() || '',
          description: isoRecord.describe_apparatus || '',
          requirements: isRequirementEnabled(isoRecord.requirements),
          items: items.map((item: any) => ({
            id: item.id,
            status: item.status_id?.toString() || '',
            key: item.key_id?.toString() || '',
            ops: item.ops || '',
          })),
        };
      });

      setExistingEarths(resolvedEarths);

      if (resolvedEarths.length > 0) {
        setEarthsSaved(true);
      }
    } catch (error: any) {
      console.error('Error fetching work permit:', error);
      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to load work permit data.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleClose = () => {
    if (editId || hasUnsavedData) {
      setShowConfirmPopup(true);
      setPendingClose(true);
    } else {
      resetAndClose();
    }
  };

  const getLocationName = (id: string) => {
    const location = locations.find((loc) => loc.id === Number(id));
    return location ? location.title : id;
  };

  const getCrossReferenceNames = (ids: string[]) => {
    if (!ids || ids.length === 0) return '-';
    const names = crossReferences.filter((ref) => ids.includes(ref.id.toString())).map((ref) => ref.reference_number);
    return names.length > 0 ? names.join(', ') : '-';
  };

  const getPlantCodeName = (id: string) => {
    const item = plantCodes.find((code) => code.id === Number(id));
    return item ? item.title : id;
  };

  const getDepartmentName = (id: string) => {
    const dept = departments.find((d) => d.id === Number(id));
    return dept ? dept.name : id;
  };

  const getResponsiblePersonLabel = (value: string) => {
    return value;
  };

  const buildApplicationPayload = (values: FormValues) => ({
    department_id: Number(values.department),
    priority: values.priority,
    ewt: values.ewt,
    ...(values.locationId ? { location_id: Number(values.locationId) } : {}),
    cross_reference_ids: (values.crossReferenceIds || []).map((crossReferenceId) => Number(crossReferenceId)),
    unit: values.unit,
    plant_code_id: Number(values.plantCodeId),
    apparatus: values.apparatus,
    description: values.hazards,
    work: values.workDescription,
  });

  const handleConfirmYes = async () => {
    setConfirmSaving(true);
    try {
      const current = liveFormValuesRef.current;
      if (current) {
        const payload = buildApplicationPayload(current);

        if (permitId) {
          await updateHVWorkPermitDraft(permitId, payload);
        } else {
          const res = await createHVWorkPermit(payload);
          setPermitId(res.data.id);
        }
      }

      if (isolationRef.current) {
        const isoValues = isolationRef.current.getValues();
        const hasPartialData = isoValues.plant_code_id || isoValues.location_id || isoValues.description || isoValues.items?.length > 0;

        if (hasPartialData) {
          const isoPayload = {
            hv_work_permit_id: permitId,
            plant_code_id: Number(isoValues.plant_code_id) || null,
            location_id: Number(isoValues.location_id) || null,
            describe_components: isoValues.description ?? '',
            requirements: isRequirementEnabled(isoValues.requirements),
            items: (isoValues.items || []).map((item: any) => ({
              status_id: item.status_id ?? item.status,
              key_id: item.key_id ?? item.key,
              ops: item.ops,
              oi_reference_ids: (Array.isArray(item.oi_reference_ids)
                ? item.oi_reference_ids
                : item.oi_reference_id != null
                  ? [item.oi_reference_id]
                  : []
              ).map(Number),
            })),
          };
          if (isolationsSaved && isoValues.id) {
            await updateIsolation(isoValues.id, isoPayload);
          } else {
            await saveIsolations(isoPayload);
          }
        }
      }

      if (earthsRef.current) {
        const earthValues = earthsRef.current.getValues();
        const hasPartialData =
          earthValues.plant_code_id || earthValues.location_id || earthValues.description || earthValues.items?.length > 0;

        if (hasPartialData) {
          const earthPayload = {
            hv_work_permit_id: permitId,
            plant_code_id: Number(earthValues.plant_code_id) || null,
            location_id: Number(earthValues.location_id) || null,
            describe_apparatus: earthValues.description ?? '',
            requirements: isRequirementEnabled(earthValues.requirements),
            items: (earthValues.items || []).map((item: any) => ({
              status_id: item.status_id ?? item.status,
              key_id: item.key_id ?? item.key,
              ops: item.ops,
            })),
          };
          if (earthsSaved && earthValues.id) {
            await updateEarth(earthValues.id, earthPayload);
          } else {
            await saveEarths(earthPayload);
          }
        }
      }

      // ADDED: persist Signature edits, if that step has data
      if (signatureRef.current) {
        const sigValues = signatureRef.current.getValues?.();
        if (sigValues?.name) {
          const signaturePayload = {
            hv_work_permit_id: permitId,
            name: sigValues.name,
            user_id: sigValues.responsible_person_id,
          };
          if (signatureId) {
            await updateSignature(signatureId, signaturePayload);
          } else {
            await saveSignature(signaturePayload);
          }
        }
      }

      setShowConfirmPopup(false);
      resetAndClose();
    } catch (error) {
      console.error('Error saving before close:', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to save changes.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      setShowConfirmPopup(false);
    } finally {
      setConfirmSaving(false);
    }
  };

  const handleConfirmNo = () => {
    setShowConfirmPopup(false);
    resetAndClose();
  };

  const resetAndClose = () => {
    setStep(0);
    setPermitId(null);
    setIsolationData(null);
    setEarthsData(null);
    setSignatureId(null);
    setSignatureData(null);
    setApplicationData(null);
    setExistingIsolations([]);
    setIsolationsSaved(false);
    setExistingEarths([]);
    setEarthsSaved(false);
    setHasUnsavedData(false);
    setIsolationDraft(emptyIsolationDraft);
    setEarthDraft(emptyEarthDraft);
    setEarthFormData(null);
    onClose();
  };

  const handleFormSubmit = async (values: FormValues, { setSubmitting }: any) => {
    try {
      setLoading(true);

      // STEP 0 – Application
      if (step === 0) {
        const payload = buildApplicationPayload(values);

        let referenceNumber = values.referenceNumber || '';
        let status = values.status || '';

        if (permitId) {
          await updateHVWorkPermitDraft(permitId, payload);
        } else {
          const res = await createHVWorkPermit(payload);
          setPermitId(res.data.id);
          referenceNumber = res.data.reference_number || '';
          status = res.data.status || '';
        }

        setApplicationData({ ...values, referenceNumber, status });
        setStep(1);
        return;
      }

      // STEP 1 – Isolations
      if (step === 1) {
        setTriggerIsolationSubmit((prev) => prev + 1);
        return;
      }

      // STEP 2 – Earths
      if (step === 2) {
        setTriggerEarthSubmit((prev) => prev + 1);
        return;
      }

      // STEP 3 – Signature
      if (step === 3) {
        if (signatureRef.current) {
          const isValid = await signatureRef.current.validateForm();
          if (!isValid) {
            return;
          }
          const sigValues = signatureRef.current.getValues();
          setSignatureData(sigValues);

          // Save signature to API
          const signaturePayload = {
            hv_work_permit_id: permitId!,
            name: sigValues.name,
            user_id: sigValues.responsible_person_id,
          };
          if (signatureId) {
            await updateSignature(signatureId, signaturePayload);
          } else {
            await saveSignature(signaturePayload);
          }
        }

        setStep(4);
        return;
      }
      // STEP 4 – Final Submit
      if (step === 4) {
        const wasEditing = !!editId;

        if (wasEditing) {
          const finalApplicationValues = applicationData ?? liveFormValuesRef.current;

          if (!finalApplicationValues) {
            throw new Error('Application details are unavailable. Please return to the Application step and try again.');
          }

          await updateHVWorkPermit(permitId!, buildApplicationPayload(finalApplicationValues));
        }

        onEditSuccess(wasEditing);
        resetAndClose();
      }
    } catch (error: any) {
      console.error(error);
      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Something went wrong. Please try again.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCancelConfirmPopup = () => {
    setShowConfirmPopup(false);
    setPendingClose(false);
  };

  const focusApplicationField = (fieldName: string) => {
    requestAnimationFrame(() => {
      const field = modalRef.current?.querySelector<HTMLElement>(`[name="${fieldName}"]`);
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field?.focus({ preventScroll: true });
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white w-full max-w-[1100px] rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col relative"
      >
        {/* Header */}
        <div className="relative px-12 pt-8">
          <h2 className="text-xl font-semibold text-gray-900">{editId ? 'Edit HV Work Permit' : 'Add HV Work Permit'}</h2>

          {/* Close Button - Top Right */}
          <button onClick={handleClose} className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 text-2xl leading-none">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Small Gap Between Heading and Stepper */}
        <div className="mt-4 px-12" />

        {/* Stepper */}

        <div className="relative">
          <div
            className="grid relative"
            style={{
              gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
            }}
          >
            <div
              className="absolute bg-gray-300"
              style={{
                top: '6px',
                left: `calc(100% / ${steps.length} / 2)`,
                right: `calc(100% / ${steps.length} / 2)`,
                height: '2px',
              }}
            />

            <div
              className="absolute bg-green-500 transition-all duration-300"
              style={{
                top: '6px',
                left: `calc(100% / ${steps.length} / 2)`,
                height: '2px',
                width: step === 0 ? '0%' : `calc(${step} * (100% - (100% / ${steps.length})) / ${steps.length - 1})`,
              }}
            />

            {steps.map((label, index) => {
              const isActive = index === step;
              const isCompleted = index < step;

              return (
                <div key={index} className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full z-10 ${isActive || isCompleted ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span
                    className={`mt-2 text-sm font-medium whitespace-nowrap ${isActive || isCompleted ? 'text-green-600' : 'text-gray-400'}`}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={getValidationSchema(step)}
          onSubmit={handleFormSubmit}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, setFieldValue, setTouched, validateForm, isSubmitting, handleSubmit }) => {
            liveFormValuesRef.current = values;

            return (
              <div className="flex-1 overflow-y-auto flex flex-col relative">
                {loading && (
                  <div className="absolute inset-0 bg-white bg-opacity-70 z-50 flex flex-col items-center justify-center gap-3">
                    <div className="relative h-12 w-12">
                      <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-gray-500 border-t-transparent animate-spin"></div>
                    </div>
                    <p className="text-sm font-medium text-gray-600">Loading...</p>
                  </div>
                )}
                <div className="flex-1 overflow-y-auto">
                  <div className="px-12 py-8">
                    {/* STEP 0 – Application */}
                    {step === 0 && (
                      <div className="space-y-6">
                        {/* Row 1 */}
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                              Department
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Field
                                as="select"
                                name="department"
                                disabled={isLoadingDepartments}
                                // ADDED: mark as unsaved when user changes a field (only for new permits)
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                  setFieldValue('department', e.target.value);
                                  setHasUnsavedData(true);
                                }}
                                className={`w-full px-4 py-2.5 border ${
                                  errors.department && touched.department ? 'border-red-500' : 'border-gray-300'
                                } rounded-md appearance-none bg-gray-100 focus:outline-none focus:border-gray-300  ${
                                  !values.department ? 'text-gray-400' : 'text-gray-900'
                                }`}
                              >
                                <option value="">{isLoadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
                                {departments.map((dept) => (
                                  <option key={dept.id} value={dept.id}>
                                    {dept.name}
                                  </option>
                                ))}
                              </Field>
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                            </div>
                            <FormError name="department" />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                              Priority
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Field
                                as="select"
                                name="priority"
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                  setFieldValue('priority', e.target.value);
                                  setHasUnsavedData(true);
                                }}
                                className={`w-full px-4 py-2.5 border ${
                                  errors.priority && touched.priority ? 'border-red-500' : 'border-gray-300'
                                } rounded-md appearance-none bg-gray-100 focus:outline-none focus:border-gray-300 ${
                                  !values.priority ? 'text-gray-400' : 'text-gray-900'
                                }`}
                              >
                                <option value="">Select Priority</option>
                                <option value="Planned Outage">Planned Outage</option>
                                <option value="Routine">Routine</option>
                                <option value="Urgent">Urgent</option>
                              </Field>
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                            </div>
                            <FormError name="priority" />
                          </div>
                        </div>

                        {/* Row 2 */}
                        <div className="grid grid-cols-2 gap-6">
                          {/* <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">Job Number</label>
                            <Field
                              type="text"
                              name="jobNumber"
                              placeholder="Enter Job Number"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue('jobNumber', e.target.value);
                                setHasUnsavedData(true);
                              }}
                              className={`w-full px-4 py-2.5 border bg-gray-100 ${
                                errors.jobNumber && touched.jobNumber ? 'border-red-500' : 'border-gray-300'
                              } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300`}
                            />
                            <FormError name="jobNumber" />
                          </div> */}

                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">EWT</label>
                            <Field
                              type="text"
                              name="ewt"
                              placeholder="Enter EWT"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue('ewt', e.target.value);
                                setHasUnsavedData(true);
                              }}
                              className={`w-full px-4 py-2.5 border bg-gray-100 ${
                                errors.ewt && touched.ewt ? 'border-red-500' : 'border-gray-300'
                              } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300`}
                            />
                            <FormError name="ewt" />
                          </div>
                        </div>

                        {/* Row 3 */}
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">Select Location</label>
                            <div className="relative">
                              <Field
                                as="select"
                                name="locationId"
                                disabled={isLoadingLocations}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                  setFieldValue('locationId', e.target.value);
                                  setHasUnsavedData(true);
                                }}
                                className={`w-full px-4 py-2.5 border ${
                                  errors.locationId && touched.locationId ? 'border-red-500' : 'border-gray-300'
                                } rounded-md appearance-none bg-gray-100 focus:outline-none focus:border-gray-300 ${
                                  !values.locationId ? 'text-gray-400' : 'text-gray-900'
                                }`}
                              >
                                <option value="">{isLoadingLocations ? 'Loading locations...' : 'Select Location'}</option>
                                {locations.map((location) => (
                                  <option key={location.id} value={location.id}>
                                    {location.title}
                                  </option>
                                ))}
                              </Field>
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                            </div>
                            <FormError name="locationId" />
                          </div>

                          <div>
                            <label className="flex items-center gap-1 text-sm font-medium text-gray-900 mb-2">
                              Cross Reference
                              <span className="text-xs font-normal text-gray-500">(Optional)</span>
                              <Info className="w-4 h-4 text-gray-400" />
                            </label>
                            <div className="relative" ref={crossRefWrapperRef}>
                              <div
                                onClick={() => setIsCrossRefDropdownOpen((prev) => !prev)}
                                className={`w-full min-h-[46px] px-3 py-2 border ${
                                  errors.crossReferenceIds && touched.crossReferenceIds ? 'border-red-500' : 'border-gray-300'
                                } rounded-md bg-gray-100 flex items-center`}
                              >
                                {/* Selected Cross References */}
                                <div
                                  ref={tagsRef}
                                  className="flex-1 flex gap-2 overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-hide"
                                >
                                  {values.crossReferenceIds?.length ? (
                                    crossReferences
                                      .filter((item) => values.crossReferenceIds.includes(item.id.toString()))
                                      .map((item) => (
                                        <span
                                          key={item.id}
                                          className="flex-shrink-0 flex items-center gap-1.5 bg-white border border-gray-200 rounded-md px-2.5 py-1 text-sm text-gray-700"
                                        >
                                          {item.reference_number}

                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();

                                              const next = values.crossReferenceIds.filter((v: string) => v !== item.id.toString());

                                              setFieldValue('crossReferenceIds', next);
                                              setHasUnsavedData(true);
                                            }}
                                            className="text-gray-400 hover:text-gray-600"
                                          >
                                            <X className="w-3.5 h-3.5" />
                                          </button>
                                        </span>
                                      ))
                                  ) : (
                                    <span className="text-gray-400 text-sm flex-shrink-0">Select Cross Reference (Optional)</span>
                                  )}
                                </div>

                                {/* Arrow */}
                                <ChevronDown className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" />
                              </div>

                              {isCrossRefDropdownOpen && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                                  {/* Search box */}
                                  <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                                    <input
                                      type="text"
                                      value={crossRefSearch}
                                      onChange={(e) => setCrossRefSearch(e.target.value)}
                                      placeholder="Search cross reference..."
                                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-gray-400"
                                      onClick={(e) => e.stopPropagation()}
                                      autoFocus
                                    />
                                  </div>
                                  {crossReferences.filter((item) =>
                                    item.reference_number?.toLowerCase().includes(crossRefSearch.toLowerCase()),
                                  ).length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-400">No options found</div>
                                  ) : (
                                    crossReferences
                                      .filter((item) => item.reference_number?.toLowerCase().includes(crossRefSearch.toLowerCase()))
                                      .map((item) => {
                                        const idStr = item.id.toString();
                                        const checked = values.crossReferenceIds?.includes(idStr) || false;

                                        return (
                                          <label
                                            key={item.id}
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={(e) => {
                                                const currentIds = values.crossReferenceIds || [];

                                                const next = e.target.checked
                                                  ? [...currentIds, idStr]
                                                  : currentIds.filter((v: string) => v !== idStr);

                                                setFieldValue('crossReferenceIds', next);
                                                setHasUnsavedData(true);

                                                setTimeout(() => {
                                                  if (tagsRef.current) {
                                                    tagsRef.current.scrollTo({
                                                      left: tagsRef.current.scrollWidth,
                                                      behavior: 'smooth',
                                                    });
                                                  }
                                                }, 0);
                                              }}
                                              className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                                            />

                                            <span className="text-gray-900">{item.reference_number}</span>
                                          </label>
                                        );
                                      })
                                  )}
                                </div>
                              )}
                            </div>
                            <FormError name="crossReferenceIds" />
                          </div>
                        </div>

                        {/* Row 4 */}
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">Unit</label>
                            <Field
                              type="text"
                              name="unit"
                              placeholder="Enter Unit"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue('unit', e.target.value);
                                setHasUnsavedData(true);
                              }}
                              className={`w-full px-4 py-2.5 border bg-gray-100 ${
                                errors.unit && touched.unit ? 'border-red-500' : 'border-gray-300'
                              } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300`}
                            />
                            <FormError name="unit" />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                              Plant Code
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Field
                                as="select"
                                name="plantCodeId"
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                  setFieldValue('plantCodeId', e.target.value);
                                  setHasUnsavedData(true);
                                }}
                                className={`w-full px-4 py-2.5 border ${
                                  errors.plantCodeId && touched.plantCodeId ? 'border-red-500' : 'border-gray-300'
                                } rounded-md appearance-none bg-gray-100 focus:outline-none focus:border-gray-300 ${
                                  !values.plantCodeId ? 'text-gray-400' : 'text-gray-900'
                                }`}
                              >
                                <option value="">Select Plant Code</option>
                                {plantCodes.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.title}
                                  </option>
                                ))}
                              </Field>
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                            </div>
                            <FormError name="plantCodeId" />
                          </div>
                        </div>

                        {/* Row 5 - Full Width */}
                        <div>
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-900 mb-2">
                            Enter Apparatus to be Worked On
                            <Info className="w-4 h-4 text-gray-400" />
                          </label>
                          <Field
                            type="text"
                            name="apparatus"
                            placeholder="Enter Apparatus to be Worked On"
                            maxLength={100}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setFieldValue('apparatus', e.target.value);
                              setHasUnsavedData(true);
                            }}
                            className={`w-full px-4 py-2.5 border bg-gray-100 ${
                              errors.apparatus && touched.apparatus ? 'border-red-500' : 'border-gray-300'
                            } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300`}
                          />
                          {/* Character counter */}
                          <div className="flex justify-end mt-1">
                            <span className={`text-xs ${100 - (values.apparatus?.length ?? 0) <= 10 ? 'text-red-500' : 'text-gray-400'}`}>
                              {100 - (values.apparatus?.length ?? 0)} characters remaining
                            </span>
                          </div>
                          <FormError name="apparatus" />
                        </div>

                        {/* Row 6 - Rich Text Editor */}
                        <div>
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-900 mb-2">
                            Describe Hazards/Precautions/Requests
                            <Info className="w-4 h-4 text-gray-400" />
                          </label>
                          <RichTextEditor
                            value={values.hazards}
                            onChange={(value: string) => {
                              setFieldValue('hazards', value);
                              setHasUnsavedData(true);
                            }}
                            placeholder="Add description here"
                            error={errors.hazards}
                            touched={touched.hazards}
                            maxLength={500}
                          />
                        </div>

                        {/* Row 7 - Rich Text Editor */}
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Describe Work to be Carried Out</label>
                          <RichTextEditor
                            value={values.workDescription}
                            onChange={(value: string) => {
                              setFieldValue('workDescription', value);
                              setHasUnsavedData(true);
                            }}
                            placeholder="Add description here"
                            error={errors.workDescription}
                            touched={touched.workDescription}
                            maxLength={800}
                          />
                        </div>
                      </div>
                    )}

                    {/* Keep data-entry steps mounted while navigating. Their
                        Formik/local state must survive Back/Next transitions. */}
                    <div className={step === 1 ? 'block' : 'hidden'} aria-hidden={step !== 1}>
                      <IsolationsAndInstruction
                        permitId={permitId}
                        ref={isolationRef}
                        onDataChange={(data) => {
                          setIsolationData(data);
                        }}
                        onValidationChange={(isValid) => {}}
                        triggerSubmit={triggerIsolationSubmit}
                        existingIsolations={existingIsolations}
                        isolationsSaved={isolationsSaved}
                        onSessionIsolationsChange={(items) => setExistingIsolations(items)}
                        onSubmitSuccess={async (data) => {
                          setTriggerIsolationSubmit(0);
                          try {
                            setLoading(true);
                            setIsolationsSaved(true);
                            setStep(2);
                          } catch (error: any) {
                            console.error(error);
                            const message =
                              error?.response?.data?.message ||
                              error?.response?.data?.error ||
                              error?.message ||
                              'Failed to save isolations';
                            setApiError(message);
                            setShowError(true);
                            setTimeout(() => setShowError(false), 5000);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        draftValues={isolationDraft}
                        onDraftChange={setIsolationDraft}
                      />
                    </div>

                    <div className={step === 2 ? 'block' : 'hidden'} aria-hidden={step !== 2}>
                      <Earths
                        permitId={permitId}
                        ref={earthsRef}
                        onDataChange={(data) => {
                          setEarthsData(data);
                        }}
                        onValidationChange={(isValid) => {}}
                        triggerSubmit={triggerEarthSubmit}
                        existingEarth={existingEarths}
                        earthsSaved={earthsSaved}
                        onSessionEarthChange={(items) => setExistingEarths(items)}
                        onSubmitSuccess={async (data) => {
                          setTriggerEarthSubmit(0);
                          try {
                            setLoading(true);
                            setEarthsSaved(true);
                            setStep(3);
                          } catch (error: any) {
                            console.error(error);
                            const message =
                              error?.response?.data?.message ||
                              error?.response?.data?.error ||
                              error?.message ||
                              'Failed to save isolations';
                            setApiError(message);
                            setShowError(true);
                            setTimeout(() => setShowError(false), 5000);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        draftValues={earthDraft}
                        onDraftChange={setEarthDraft}
                      />
                    </div>

                    <div className={step === 3 ? 'block' : 'hidden'} aria-hidden={step !== 3}>
                      <Signature
                        ref={signatureRef}
                        permitId={permitId}
                        initialConfirmed={Boolean(signatureData?.confirmed)}
                        onChange={setSignatureData}
                      />
                    </div>

                    {/* STEP 4 - Preview & Add / Update */}
                    {step === 4 && applicationData && (
                      <div className="space-y-6">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-gray-900 font-semibold text-lg">{applicationData.referenceNumber || '-'}</p>
                          </div>
                          <div>
                            <span
                              className={`inline-flex items-center gap-1.5 text-sm font-medium ${getStatusColors(applicationData.status || 'New').text}`}
                            >
                              <span className={`w-2 h-2 rounded-full ${getStatusColors(applicationData.status || 'New').dot}`}></span>
                              {applicationData.status || 'New'}
                            </span>
                          </div>
                        </div>
                        {/* Application Section */}
                        <div>
                          <h3 className="text-lg font-semibold text-blue-600 mb-4">Application</h3>
                          <div className="grid grid-cols-6 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600 mb-1">Department</p>
                              <p className="text-gray-900 font-medium">{getDepartmentName(applicationData.department) || '-'}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 mb-1">Priority</p>
                              <p className="text-gray-900 font-medium">{applicationData.priority || '-'}</p>
                            </div>
                            {/* <div>
                              <p className="text-gray-600 mb-1">Job Number</p>
                              <p className="text-gray-900 font-medium">{applicationData.jobNumber || '-'}</p>
                            </div> */}
                            <div>
                              <p className="text-gray-600 mb-1">EWT</p>
                              <p className="text-gray-900 font-medium">{applicationData.ewt || '-'}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 mb-1">Location</p>
                              <p className="text-gray-900 font-medium">{getLocationName(applicationData.locationId) || '-'}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 mb-1">Cross Reference</p>
                              <p className="text-gray-900 font-medium">
                                {getCrossReferenceNames(applicationData.crossReferenceIds) || '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-6 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600 mb-1">Unit</p>
                            <p className="text-gray-900 font-medium">{applicationData.unit || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 mb-1">Plant Code</p>
                            <p className="text-gray-900 font-medium">{getPlantCodeName(applicationData.plantCodeId) || '-'}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-600 mb-1">Apparatus to be worked On</p>
                            <p className="text-gray-900 font-medium">{applicationData.apparatus || '-'}</p>
                          </div>
                        </div>

                        {/* Hazards Section */}

                        <p className="text-gray-600 mb-2 text-sm">Hazards/Precautions/Requests</p>

                        <div className="text-gray-900 text-base max-w-full">
                          {applicationData.hazards ? (
                            Array.isArray(applicationData.hazards) ? (
                              <ul className="list-disc list-inside space-y-1 break-words">
                                {applicationData.hazards.map((hazard: string, index: number) => (
                                  <li key={index} className="break-words">
                                    {hazard}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <ul className="list-disc list-inside space-y-1 break-words">
                                {applicationData.hazards
                                  .split('\n')
                                  .map((h: string) => h.trim())
                                  .filter((h: string) => h.length > 0)
                                  .map((hazard: string, index: number) => (
                                    <li key={index} className="break-words">
                                      {hazard}
                                    </li>
                                  ))}
                              </ul>
                            )
                          ) : (
                            '-'
                          )}
                        </div>

                        {/* Work to be Carried Out Section */}

                        <p className="text-gray-600 mb-2 text-sm">Work to be Carried Out</p>

                        <div className="text-gray-900 text-base max-w-full">
                          {applicationData.workDescription ? (
                            Array.isArray(applicationData.workDescription) ? (
                              <ul className="list-disc list-inside space-y-1 break-words">
                                {applicationData.workDescription.map((item: string, index: number) => (
                                  <li key={index} className="break-words">
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <ul className="list-disc list-inside space-y-1 break-words">
                                {applicationData.workDescription
                                  .split('\n')
                                  .map((w: string) => w.trim())
                                  .filter((w: string) => w.length > 0)
                                  .map((item: string, index: number) => (
                                    <li key={index} className="break-words">
                                      {item}
                                    </li>
                                  ))}
                              </ul>
                            )
                          ) : (
                            '-'
                          )}
                        </div>

                        {/* Isolations/Instructions Section */}
                        {existingIsolations.length > 0 && existingIsolations[0].items?.length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold text-blue-600 mb-4">Isolations/Instructions</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm mb-4 p-4 bg-gray-50 rounded-md">
                              <div>
                                <p className="text-gray-600 mb-1">Plant Code</p>
                                <p className="text-gray-900 font-medium">{existingIsolations[0].plant_code?.title || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 mb-1">Location</p>
                                <p className="text-gray-900 font-medium">{existingIsolations[0].location?.title || 'N/A'}</p>
                              </div>
                              <div className="col-span-2">
                                <p className="text-gray-600 mb-1">Description</p>
                                <p className="text-gray-900 font-medium whitespace-pre-wrap break-words">
                                  {existingIsolations[0].description || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600 mb-1">Requirements</p>
                                <p className="text-gray-900 font-medium">
                                  {isRequirementEnabled(existingIsolations[0].requirements) ? 'Yes' : 'No'}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-3">
                              {existingIsolations[0].items.map((item: any, index: number) => {
                                const statusLabel =
                                  statusOptions.find((s: any) => s.id === Number(item.status))?.title || item.status || 'Pending';
                                const keyLabel = keyOptions.find((k: any) => k.id === Number(item.key))?.title || item.key || 'N/A';
                                const colors = getStatusColor(statusLabel);
                                const itemKey = `preview-isolation-item-${item.id ?? index}`;

                                return (
                                  <div key={itemKey} className="border border-gray-300 rounded-md">
                                    <button
                                      type="button"
                                      onClick={() => setExpandedItem(expandedItem === itemKey ? null : itemKey)}
                                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                    >
                                      <div className="flex items-center space-x-2">
                                        <span className="text-sm font-medium text-gray-900">
                                          {index + 1}. {item.ops || 'Operational Instruction'}
                                        </span>
                                        <span className="flex items-center space-x-1.5">
                                          <span className={`w-2 h-2 rounded-full ${colors.dot}`}></span>
                                          <span className={`text-sm ${colors.text} font-normal`}>{statusLabel}</span>
                                        </span>
                                      </div>
                                      <ChevronDown
                                        className={`w-5 h-5 text-gray-600 transition-transform ${expandedItem === itemKey ? 'transform rotate-180' : ''}`}
                                      />
                                    </button>

                                    {expandedItem === itemKey && (
                                      <div className="px-4 py-3 border-t border-gray-300 bg-gray-50">
                                        <div className="space-y-2 text-sm">
                                          <p className="text-gray-600 break-words">
                                            <span className="font-medium">OPS:</span> {item.ops || 'N/A'}
                                          </p>
                                          <p className="text-gray-600">
                                            <span className="font-medium">Key:</span> {keyLabel}
                                          </p>
                                          <p className="text-gray-600 break-words">
                                            <span className="font-medium">OI Reference:</span> {getOiReferenceLabels(item)}
                                          </p>
                                          <p className="text-gray-600">
                                            <span className="font-medium">Status:</span> {statusLabel}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Earths Section */}
                        {existingEarths.length > 0 && existingEarths[0].items?.length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold text-blue-600 mb-4">Earths</h3>

                            <div className="grid grid-cols-2 gap-4 text-sm mb-4 p-4 bg-gray-50 rounded-md">
                              <div>
                                <p className="text-gray-600 mb-1">Plant Code</p>
                                <p className="text-gray-900 font-medium">{existingEarths[0].plant_code?.title || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 mb-1">Location</p>
                                <p className="text-gray-900 font-medium">{existingEarths[0].location?.title || 'N/A'}</p>
                              </div>
                              <div className="col-span-2">
                                <p className="text-gray-600 mb-1">Description</p>
                                <p className="text-gray-900 font-medium whitespace-pre-wrap break-words">
                                  {existingEarths[0].description || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600 mb-1">Requirements</p>
                                <p className="text-gray-900 font-medium">
                                  {isRequirementEnabled(existingEarths[0].requirements) ? 'Yes' : 'No'}
                                </p>
                              </div>
                            </div>

                            {/* Optional summary totals, if present on the record */}
                            {(existingEarths[0].total_earth_required || existingEarths[0].total_earth_applied) && (
                              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                <div>
                                  <p className="text-gray-600 mb-1">Total Earth Required</p>
                                  <p className="text-gray-900 font-medium">{existingEarths[0].total_earth_required || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 mb-1">Total Earths Applied</p>
                                  <p className="text-gray-900 font-medium">{existingEarths[0].total_earth_applied || '-'}</p>
                                </div>
                              </div>
                            )}

                            <div className="space-y-3">
                              {existingEarths[0].items.map((item: any, index: number) => {
                                const statusLabel =
                                  statusOptions.find((s: any) => s.id === Number(item.status))?.title || item.status || 'Pending';
                                const keyLabel = keyOptions.find((k: any) => k.id === Number(item.key))?.title || item.key || 'N/A';
                                const colors = getStatusColor(statusLabel);
                                const itemKey = `preview-earth-item-${item.id ?? index}`;

                                return (
                                  <div key={itemKey} className="border border-gray-300 rounded-md">
                                    <button
                                      type="button"
                                      onClick={() => setExpandedItem(expandedItem === itemKey ? null : itemKey)}
                                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                    >
                                      <div className="flex items-center space-x-2">
                                        <span className="text-sm font-medium text-gray-900">
                                          {index + 1}. {item.ops || 'Operational Instruction'}
                                        </span>
                                        <span className="flex items-center space-x-1.5">
                                          <span className={`w-2 h-2 rounded-full ${colors.dot}`}></span>
                                          <span className={`text-sm ${colors.text} font-normal`}>{statusLabel}</span>
                                        </span>
                                      </div>
                                      <ChevronDown
                                        className={`w-5 h-5 text-gray-600 transition-transform ${expandedItem === itemKey ? 'transform rotate-180' : ''}`}
                                      />
                                    </button>

                                    {expandedItem === itemKey && (
                                      <div className="px-4 py-3 border-t border-gray-300 bg-gray-50">
                                        <div className="space-y-2 text-sm">
                                          <p className="text-gray-600 break-words">
                                            <span className="font-medium">OPS:</span> {item.ops || 'N/A'}
                                          </p>
                                          <p className="text-gray-600">
                                            <span className="font-medium">Key:</span> {keyLabel}
                                          </p>
                                          <p className="text-gray-600">
                                            <span className="font-medium">Status:</span> {statusLabel}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Signature Section */}
                        {signatureData && (
                          <div className="">
                            <h3 className="text-lg font-semibold text-blue-600 mb-4">Signature</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 mb-1">Name</p>
                                <p className="text-gray-900 font-medium">{signatureData.name || '-'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 mb-1">Role</p>
                                <p className="text-gray-900 font-medium">{signatureData.designation || '-'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-12 py-6 border-t">
                  {step > 0 && (
                    <button
                      type="button"
                      onClick={() => setStep(step - 1)}
                      className="px-6 py-2.5 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50"
                    >
                      {step === 4 ? 'Previous' : 'Back'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-6 py-2.5 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (step === 0) {
                        const validationErrors = await validateForm();
                        const firstInvalidField = ['department', 'priority', 'plantCodeId'].find((field) => validationErrors[field]);

                        if (firstInvalidField) {
                          await setTouched(
                            {
                              department: true,
                              priority: true,
                              plantCodeId: true,
                            },
                            false,
                          );
                          focusApplicationField(firstInvalidField);
                          return;
                        }
                      }

                      if (step === 1 && isolationRef.current && !(await isolationRef.current.validateForm())) return;
                      if (step === 2 && earthsRef.current && !(await earthsRef.current.validateForm())) return;

                      handleSubmit();
                    }}
                    disabled={loading || isSubmitting}
                    className="px-8 py-2.5 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading || isSubmitting ? 'Loading...' : step === steps.length - 1 ? (editId ? 'Update' : 'Add') : 'Next'}
                  </button>
                </div>
              </div>
            );
          }}
        </Formik>

        {/* Error Notification */}
        {showError && (
          <div className="fixed bottom-6 right-6 z-[100]">
            <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
              <button
                onClick={() => setShowError(false)}
                className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold mb-1">Error</h4>
                  <p className="text-sm">{apiError}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* CHANGED: Save popup only shows for new permit with unsaved data (not in edit mode) */}
        {showConfirmPopup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-lg p-6 w-[350px] relative">
              <button
                onClick={handleCancelConfirmPopup}
                disabled={confirmSaving}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-lg font-semibold mb-4">Save Changes?</h3>
              <p className="text-sm text-gray-600 mb-6">Do you want to save before closing?</p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleConfirmNo}
                  disabled={confirmSaving}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  No
                </button>
                <button
                  onClick={handleConfirmYes}
                  disabled={confirmSaving}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2 min-w-[70px] justify-center"
                >
                  {confirmSaving ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Yes'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HVWorkPermitModal;
